import { collection, getDocs, doc, getDoc, query, orderBy, limit, where } from "firebase/firestore"
import { db } from "../firebase"
import { calculateDistance } from "../utils/distanceUtils"
import { generateGeohashQueries, getMapBounds, isPointInBounds } from "../utils/geohashUtils"

/**
 * Fetch locations using geohash queries (optimized for map regions)
 * @param {Object} region - Region object with latitude, longitude, latitudeDelta, longitudeDelta
 * @param {number} limitCount - Maximum number of locations to return
 * @param {Object} filters - Optional filters to apply
 * @returns {Promise<Array>} Array of location objects with bathrooms
 */
export const fetchLocationsGeohashed = async (region, limitCount = 200, filters = {}) => {
  try {
    if (!region || !region.latitude || !region.longitude) {
      throw new Error("Invalid region provided")
    }

    const bounds = getMapBounds(region)

    // Calculate search radius from region bounds
    const radiusMiles =
      calculateDistance(
        region.latitude - region.latitudeDelta / 2,
        region.longitude - region.longitudeDelta / 2,
        region.latitude + region.latitudeDelta / 2,
        region.longitude + region.longitudeDelta / 2,
      ) / 2

    // Generate geohash queries for the region
    const geohashQueries = generateGeohashQueries(region.latitude, region.longitude, radiusMiles)

    // Execute parallel geohash queries
    const locationsRef = collection(db, "locations_geohashed")
    const queryPromises = geohashQueries.neighbors.map(async geohash => {
      const q = query(
        locationsRef,
        where("geohash", ">=", geohash),
        where("geohash", "<=", geohash + "\uf8ff"),
        limit(Math.ceil(limitCount / geohashQueries.neighbors.length)),
      )
      return getDocs(q)
    })

    const queryResults = await Promise.all(queryPromises)
    const seenIds = new Set()
    let locations = []

    // Process query results and remove duplicates
    queryResults.forEach(querySnapshot => {
      querySnapshot.forEach(doc => {
        if (!seenIds.has(doc.id)) {
          seenIds.add(doc.id)
          const data = doc.data()

          if (data.latitude && data.longitude) {
            // Verify location is within map bounds
            if (isPointInBounds(data.latitude, data.longitude, bounds)) {
              const distance = calculateDistance(region.latitude, region.longitude, data.latitude, data.longitude)

              locations.push({
                id: doc.id,
                ...data,
                distance: distance,
                // Convert Firestore timestamps to JavaScript dates
                createdAt: data.createdAt?.toDate(),
                updatedAt: data.updatedAt?.toDate(),
                geoCreatedAt: data.geoCreatedAt?.toDate(),
                migratedAt: data.migratedAt?.toDate(),
                // Process bathrooms array if it exists
                bathrooms: data.bathrooms
                  ? data.bathrooms.map(bathroom => ({
                      ...bathroom,
                      createdAt: bathroom.createdAt?.toDate(),
                      updatedAt: bathroom.updatedAt?.toDate(),
                      migratedAt: bathroom.migratedAt?.toDate(),
                      lastFlaggedAt: bathroom.lastFlaggedAt?.toDate(),
                    }))
                  : [],
              })
            }
          }
        }
      })
    })

    // Sort by distance and limit results
    locations.sort((a, b) => a.distance - b.distance)
    const finalResults = locations.slice(0, limitCount)
    return finalResults
  } catch (error) {
    console.error("❌ Geohash query failed:", error)
    throw error
  }
}

/**
 * Fetch all bathrooms from locations (flattened array)
 * @param {Object} region - Optional region to limit search area
 * @returns {Promise<Array>} Array of bathroom objects
 */
export const fetchAllBathrooms = async (region = null) => {
  try {
    let locations

    if (region) {
      // Use geohash queries if region is provided
      // Increase limit to handle larger search areas
      locations = await fetchLocationsGeohashed(region, 1000)
    } else {
      // Fallback: fetch all locations (less efficient)
      const locationsRef = collection(db, "locations_geohashed")
      const locationsSnapshot = await getDocs(locationsRef)
      locations = locationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }))
    }

    // Flatten bathrooms from all locations
    const bathrooms = []
    locations.forEach(location => {
      if (location.bathrooms && Array.isArray(location.bathrooms)) {
        location.bathrooms.forEach(bathroom => {
          bathrooms.push({
            ...bathroom,
            locationId: location.id,
            locationName: location.name,
            // Use bathroom coordinates if available, otherwise use location coordinates
            latitude: bathroom.latitude || location.latitude,
            longitude: bathroom.longitude || location.longitude,
            distance: location.distance,
            // Copy amenities from location if bathroom doesn't have them
            amenities: bathroom.amenities || location.amenities,
          })
        })
      }
    })

    return bathrooms
  } catch (error) {
    console.error("Error fetching bathrooms:", error)
    throw error
  }
}

/**
 * Fetch bathrooms within a certain distance of user location
 * @param {number} userLat - User's latitude
 * @param {number} userLon - User's longitude
 * @param {number} maxDistance - Maximum distance in miles
 * @returns {Promise<Array>} Array of nearby bathroom objects with distance
 */
export const fetchNearbyBathrooms = async (userLat, userLon, maxDistance = 25) => {
  try {
    // Create a region based on user location and max distance
    // Rough conversion: 1 degree latitude ≈ 69 miles
    const latDelta = (maxDistance / 69) * 2
    const lonDelta = (maxDistance / 69) * 2

    const region = {
      latitude: userLat,
      longitude: userLon,
      latitudeDelta: latDelta,
      longitudeDelta: lonDelta,
    }

    // Use geohash queries for efficient nearby search
    const bathrooms = await fetchAllBathrooms(region)

    // Filter by exact distance and sort
    const nearbyBathrooms = bathrooms
      .map(bathroom => {
        if (!bathroom.latitude || !bathroom.longitude) return null

        const distance = calculateDistance(userLat, userLon, bathroom.latitude, bathroom.longitude)

        return {
          ...bathroom,
          distance: Math.round(distance * 10) / 10, // Round to 1 decimal
        }
      })
      .filter(bathroom => bathroom && bathroom.distance <= maxDistance)
      .sort((a, b) => a.distance - b.distance)

    return nearbyBathrooms
  } catch (error) {
    console.error("Error fetching nearby bathrooms:", error)
    throw error
  }
}

/**
 * Fetch a single bathroom by ID
 * @param {string} bathroomId - Bathroom document ID (format: "locationId/bathroomId" or just "bathroomId")
 * @returns {Promise<Object>} Bathroom object
 */
export const fetchBathroomById = async bathroomId => {
  try {
    // If bathroomId contains a slash, it's in format "locationId/bathroomId"
    if (bathroomId.includes("/")) {
      const [locationId, actualBathroomId] = bathroomId.split("/")

      // Fetch the location document
      const locationRef = doc(db, "locations_geohashed", locationId)
      const locationDoc = await getDoc(locationRef)

      if (!locationDoc.exists()) {
        throw new Error(`Location not found: ${locationId}`)
      }

      const locationData = locationDoc.data()

      // Find the bathroom in the bathrooms array
      const bathroom = locationData.bathrooms?.find(b => b.id === actualBathroomId)

      if (!bathroom) {
        throw new Error(`Bathroom not found: ${actualBathroomId}`)
      }

      return {
        ...bathroom,
        locationId: locationId,
        locationName: locationData.name,
        // Include location coordinates if not in bathroom data
        latitude: bathroom.latitude || locationData.latitude,
        longitude: bathroom.longitude || locationData.longitude,
        createdAt: bathroom.createdAt?.toDate(),
        updatedAt: bathroom.updatedAt?.toDate(),
      }
    } else {
      // Search all locations for this bathroom ID (less efficient, but backwards compatible)
      const locationsRef = collection(db, "locations_geohashed")
      const locationsSnapshot = await getDocs(locationsRef)

      for (const locationDoc of locationsSnapshot.docs) {
        const locationData = locationDoc.data()
        const bathroom = locationData.bathrooms?.find(b => b.id === bathroomId)

        if (bathroom) {
          return {
            ...bathroom,
            locationId: locationDoc.id,
            locationName: locationData.name,
            latitude: bathroom.latitude || locationData.latitude,
            longitude: bathroom.longitude || locationData.longitude,
            createdAt: bathroom.createdAt?.toDate(),
            updatedAt: bathroom.updatedAt?.toDate(),
          }
        }
      }

      throw new Error("Bathroom not found")
    }
  } catch (error) {
    console.error("Error fetching bathroom:", error)
    throw error
  }
}

/**
 * Fetch reviews for a specific bathroom
 * Full reviews live in locations_geohashed/{locationId}/bathrooms/{bathroomId}/reviews
 * @param {string} bathroomId - Bathroom document ID (format: "locationId/bathroomId" or just "bathroomId")
 * @returns {Promise<Array>} Array of review objects
 */
export const fetchBathroomReviews = async bathroomId => {
  try {
    const bathroom = await fetchBathroomById(bathroomId)

    if (!bathroom?.locationId || !bathroom?.id) {
      return []
    }

    const reviewsSnap = await getDocs(collection(db, "locations_geohashed", bathroom.locationId, "bathrooms", bathroom.id, "reviews"))

    const processedReviews = reviewsSnap.docs
      .map(reviewDoc => {
        const data = reviewDoc.data()
        return {
          id: reviewDoc.id,
          ...data,
          timestamp: data.timestamp?.toDate ? data.timestamp.toDate() : data.timestamp,
        }
      })
      .sort((a, b) => {
        const dateA = a.timestamp instanceof Date ? a.timestamp : new Date(a.timestamp || 0)
        const dateB = b.timestamp instanceof Date ? b.timestamp : new Date(b.timestamp || 0)
        return dateB - dateA
      })

    return processedReviews
  } catch (error) {
    console.error("Error fetching bathroom reviews:", error)
    return [] // Return empty array on error
  }
}

/**
 * Fetch recent bathrooms (newest first)
 * @param {number} limitCount - Number of bathrooms to fetch
 * @returns {Promise<Array>} Array of recent bathroom objects
 */
export const fetchRecentBathrooms = async (limitCount = 10) => {
  try {
    const bathroomsRef = collection(db, "bathrooms")
    const recentQuery = query(bathroomsRef, orderBy("createdAt", "desc"), limit(limitCount))
    const snapshot = await getDocs(recentQuery)

    const bathrooms = []
    snapshot.forEach(doc => {
      const data = doc.data()
      bathrooms.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
      })
    })

    return bathrooms
  } catch (error) {
    console.error("Error fetching recent bathrooms:", error)
    throw error
  }
}
